import React, { useMemo, useState } from "react";
import toast from "react-hot-toast";
import API from "../../utils/api";
import { useAuth } from "../../App";
import { celebrate } from "../../components/CelebrationHub";

// A lead added here never came from walking into an institution — no GPS,
// no photo, nothing to evidence. Just where it came from.
const LEAD_SOURCES = [
  { value: "phone_inquiry", label: "Phone Enquiry",  desc: "They called or messaged in" },
  { value: "referral",      label: "Referral",       desc: "Someone pointed them our way" },
  { value: "walk_in",       label: "Office Walk-in", desc: "They came to us, not the other way" },
  { value: "exhibition",    label: "Exhibition",     desc: "Met at a fair, expo or event" },
  { value: "website",       label: "Website",        desc: "Submitted an enquiry form online" },
  { value: "other",         label: "Other",          desc: "" },
];

const LEAD_STATUSES = [
  { key: "hot",  label: "Hot",  desc: "Ready to convert",    color: "#dc2626", bg: "#fee2e2" },
  { key: "warm", label: "Warm", desc: "Interested, nurture", color: "#b45309", bg: "#fef9c3" },
  { key: "cold", label: "Cold", desc: "Not ready yet",       color: "#1d4ed8", bg: "#dbeafe" },
];

const EBEK_INSTITUTION_TYPES = [
  { value: "school",              label: "School" },
  { value: "college",             label: "College" },
  { value: "engineering_college", label: "Engineering College" },
  { value: "university",          label: "University" },
  { value: "polytechnic",         label: "Polytechnic" },
  { value: "other",               label: "Other" },
];

const EBEKMED_INSTITUTION_TYPES = [
  { value: "nursing_college",      label: "Nursing College" },
  { value: "nursing_university",   label: "Nursing University" },
  { value: "skills_center",        label: "Skills Centers" },
  { value: "hospital",             label: "Hospitals" },
  { value: "corporate_office",     label: "Corporate Office" },
  { value: "other",                label: "Others" },
];

const EBEK_INTERESTED_IN_OPTIONS = [
  { value: "ebek_lpi",     label: "EBEK LPI"     },
  { value: "bookr",        label: "Bookr"        },
  { value: "ceq",          label: "CEQ"          },
  { value: "linguaskill",  label: "Linguaskill"  },
  { value: "gla",          label: "GLA"          },
  { value: "others",       label: "Others"       },
];

const EBEKMED_INTERESTED_IN_OPTIONS = [
  { value: "inc_mandatory_modules", label: "INC mandatory Modules" },
  { value: "osce_solutions", label: "OSCE Solutions" },
  { value: "online_competency_modules", label: "Online Competency modules" },
  { value: "hands_on_training", label: "Hands-On Training" },
  { value: "competency_management_solutions", label: "Competency Management solutions" },
  { value: "others", label: "Others" },
];

export default function AddLead({ onDone, accent }) {
  const { user } = useAuth();
  const [source, setSource] = useState("");
  const [form, setForm] = useState({
    institution_name: "", institution_type: "", city: "",
    contact_name: "", contact_phone: "", contact_email: "",
    lead_status: "warm", interested_in: "", notes: "",
    student_count: "", value_per_student: "", next_follow_up: "",
  });
  const [interestOther, setInterestOther] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [duplicateLeads, setDuplicateLeads] = useState([]);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const institutionTypes = user?.company === "ebekmed" ? EBEKMED_INSTITUTION_TYPES : EBEK_INSTITUTION_TYPES;
  const interestedOptions = user?.company === "ebekmed" ? EBEKMED_INTERESTED_IN_OPTIONS : EBEK_INTERESTED_IN_OPTIONS;

  const approxRevenue = useMemo(() => {
    const students = Number(form.student_count || 0);
    const perStudent = Number(form.value_per_student || 0);
    return students > 0 && perStudent > 0 ? students * perStudent : 0;
  }, [form.student_count, form.value_per_student]);

  const minDate = (() => {
    const d = new Date(); d.setDate(d.getDate() + 1);
    return d.toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
  })();

  const checkForDuplicateLead = async () => {
    if (form.institution_name.trim().length < 3) return;
    setCheckingDuplicate(true);
    try {
      const response = await API.get("/leads/duplicate-check", {
        params: {
          institution_name: form.institution_name.trim(),
          contact_email: form.contact_email.trim() || undefined,
          contact_phone: form.contact_phone.trim() || undefined,
          city: form.city.trim() || undefined,
        },
      });
      setDuplicateLeads(Array.isArray(response.data) ? response.data : []);
    } catch {
      setDuplicateLeads([]);
    } finally {
      setCheckingDuplicate(false);
    }
  };

  const handleSubmit = async () => {
    if (!source) return toast.error("How did this lead come in?");
    if (!form.institution_name.trim()) return toast.error("Institution name is required");
    if (!form.contact_phone.trim() && !form.contact_email.trim()) {
      return toast.error("Add a phone number or email so this lead can be followed up");
    }
    if (form.contact_email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.contact_email.trim())) {
      return toast.error("That email address does not look right");
    }
    setSubmitting(true);
    const payload = {
      ...form,
      lead_source: source,
      interested_in: form.interested_in === "others" && interestOther.trim() ? interestOther.trim() : form.interested_in,
      student_count: form.student_count || null,
      value_per_student: form.value_per_student || null,
      next_follow_up: form.next_follow_up || null,
    };
    try {
      await API.post("/leads", payload);
      toast.success("Lead added ✨");
      celebrate("visitComplete", { message: form.institution_name ? `${form.institution_name} added to your pipeline` : "Lead added to your pipeline" });
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to add lead");
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = {
    width: "100%", padding: "12px 14px", borderRadius: 12,
    border: "1.5px solid #e2e8f0", fontSize: 16, fontFamily: "inherit",
    outline: "none", background: "#fff", boxSizing: "border-box",
  };
  const grid2 = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 };
  const labelStyle = {
    fontSize: 12, fontWeight: 700, color: "#64748b", marginBottom: 6,
    display: "block", textTransform: "uppercase", letterSpacing: 0.5,
  };
  const cardStyle = {
    background: "#fff", border: "1px solid #e2e8f0",
    borderRadius: 16, padding: 16, marginBottom: 12,
  };

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 20, fontWeight: 800, color: "#1e293b", marginBottom: 4 }}>Add Lead</div>
        <div style={{ fontSize: 13, color: "#64748b" }}>For a lead that didn't come from a field visit</div>
      </div>

      {/* Where this lead came from */}
      <div style={cardStyle}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>Where did this come from? *</div>
        <div style={{ fontSize: 11, color: "#64748b", margin: "4px 0 12px" }}>
          No visit was made — this just gets it into the pipeline.
        </div>
        <div style={{ display: "grid", gap: 9 }}>
          {LEAD_SOURCES.map(option => {
            const sel = source === option.value;
            return (
              <button key={option.value} type="button" onClick={() => setSource(option.value)}
                style={{
                  display: "flex", alignItems: "center", gap: 12, width: "100%",
                  padding: "12px 14px", borderRadius: 13, cursor: "pointer",
                  border: `1.5px solid ${sel ? accent : "#e2e8f0"}`,
                  background: sel ? "#f0fdfa" : "#fff",
                  textAlign: "left", fontFamily: "inherit",
                }}>
                <span style={{ width: 22, height: 22, flex: "0 0 22px", borderRadius: "50%", border: `2px solid ${sel ? accent : "#cbd5e1"}`, background: sel ? accent : "#fff", color: "#fff", display: "grid", placeItems: "center", fontWeight: 900, fontSize: 12 }}>{sel ? "✓" : ""}</span>
                <span>
                  <strong style={{ display: "block", fontSize: 14, color: "#172033" }}>{option.label}</strong>
                  {option.desc && <small style={{ color: "#64748b" }}>{option.desc}</small>}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Institution details */}
      <div style={cardStyle}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", marginBottom: 12 }}>Institution Details</div>
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Institution *</label>
          <input style={inputStyle} value={form.institution_name}
            onChange={e => { set("institution_name", e.target.value); setDuplicateLeads([]); }}
            onBlur={checkForDuplicateLead}
            placeholder="School / College / Organisation" />
          {checkingDuplicate && <div style={{ marginTop: 7, fontSize: 11, color: "#64748b" }}>Checking for existing leads...</div>}
          {duplicateLeads.length > 0 && (
            <div role="alert" style={{ marginTop: 8, padding: "10px 12px", borderRadius: 10, border: "1px solid #fbbf24", background: "#fffbeb", color: "#92400e", fontSize: 11, lineHeight: 1.5 }}>
              <div style={{ fontWeight: 800, marginBottom: 4 }}>Possible existing lead found</div>
              {duplicateLeads.slice(0, 3).map(match => (
                <div key={match.lead_id} style={{ marginTop: 4 }}>
                  {match.institution_name} · Owner: {match.owner_name} · {(match.pipeline_stage || "visited").replaceAll("_", " ")}
                </div>
              ))}
              {duplicateLeads.some(match => Number(match.user_id) !== Number(user?.id)) && <div style={{ marginTop: 5 }}>Ask your manager to transfer the lead before creating another record.</div>}
            </div>
          )}
        </div>
        <div style={{ ...grid2, marginBottom: 12 }}>
          <div>
            <label style={labelStyle}>Institution Type</label>
            <select value={form.institution_type} onChange={e => set("institution_type", e.target.value)}
              style={{ ...inputStyle, appearance: "none", cursor: "pointer" }}>
              <option value="">Select type</option>
              {institutionTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>City</label>
            <input style={inputStyle} value={form.city} onChange={e => set("city", e.target.value)} placeholder="e.g. Chennai" />
          </div>
        </div>
        <div style={{ ...grid2, marginBottom: 12 }}>
          <div>
            <label style={labelStyle}>Phone</label>
            <input style={inputStyle} type="tel" value={form.contact_phone} onChange={e => set("contact_phone", e.target.value)} placeholder="+91 XXXXX" />
          </div>
          <div>
            <label style={labelStyle}>Email</label>
            <input style={inputStyle} type="email" value={form.contact_email} onChange={e => set("contact_email", e.target.value)} placeholder="email@school.in" />
          </div>
        </div>
        <div>
          <label style={labelStyle}>Contact Person</label>
          <input style={inputStyle} value={form.contact_name} onChange={e => set("contact_name", e.target.value)} placeholder="Who you spoke with" />
        </div>
      </div>

      {/* Lead status */}
      <div style={cardStyle}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", marginBottom: 12 }}>Lead Status</div>
        <div style={{ display: "flex", gap: 10 }}>
          {LEAD_STATUSES.map(s => (
            <button key={s.key} onClick={() => set("lead_status", s.key)}
              style={{ flex: 1, padding: "12px 8px", border: `2px solid ${form.lead_status === s.key ? s.color : "#e2e8f0"}`, borderRadius: 12, background: form.lead_status === s.key ? s.bg : "#fff", cursor: "pointer", fontFamily: "inherit", transition: "all 0.2s" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: form.lead_status === s.key ? s.color : "#64748b" }}>{s.label}</div>
              <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 2 }}>{s.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Interested in */}
      <div style={cardStyle}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", marginBottom: 12 }}>Interested In</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {interestedOptions.map(opt => {
            const sel = form.interested_in === opt.value;
            return (
              <button key={opt.value} onClick={() => set("interested_in", sel ? "" : opt.value)}
                style={{
                  padding: "8px 16px", borderRadius: 20,
                  border: `1.5px solid ${sel ? accent : "#e2e8f0"}`,
                  background: sel ? accent : "#f8fafc",
                  color: sel ? "#fff" : "#475569",
                  fontWeight: 600, fontSize: 13, cursor: "pointer",
                  fontFamily: "inherit", transition: "all 0.2s",
                }}>
                {opt.label}
              </button>
            );
          })}
        </div>
        {form.interested_in === "others" && (
          <input style={{ ...inputStyle, marginTop: 10 }} value={interestOther}
            onChange={e => setInterestOther(e.target.value)} placeholder="Type product / service name" />
        )}
      </div>

      {/* Student count & revenue */}
      <div style={cardStyle}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", marginBottom: 12 }}>Student Count & Revenue Estimate</div>
        <div style={{ ...grid2, marginBottom: 12 }}>
          <div>
            <label style={labelStyle}>No. of Students</label>
            <input style={inputStyle} type="number" min="0" value={form.student_count} onChange={e => set("student_count", e.target.value)} placeholder="e.g. 500" />
          </div>
          <div>
            <label style={labelStyle}>Value Per Student (₹)</label>
            <input style={inputStyle} type="number" min="0" step="0.01" value={form.value_per_student} onChange={e => set("value_per_student", e.target.value)} placeholder="e.g. 1200" />
          </div>
        </div>
        {approxRevenue > 0 && (
          <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 12, padding: "12px 14px", color: "#15803d", fontSize: 13, fontWeight: 800 }}>
            Approx Revenue: Rs. {approxRevenue.toLocaleString("en-IN")}
          </div>
        )}
      </div>

      {/* Notes */}
      <div style={cardStyle}>
        <label style={labelStyle}>Notes</label>
        <textarea value={form.notes} onChange={e => set("notes", e.target.value)}
          placeholder="Anything useful — what they asked about, who referred them, what was said"
          style={{ ...inputStyle, resize: "none", minHeight: 90 }} />
      </div>

      {/* Follow-up */}
      <div style={{ ...cardStyle, border: `1.5px solid ${form.next_follow_up ? "#7c3aed" : "#e2e8f0"}`, background: form.next_follow_up ? "#faf5ff" : "#fff" }}>
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>Schedule Follow-up</div>
          <div style={{ fontSize: 11, color: "#7c3aed" }}>Reminder will be sent 1 day before via WhatsApp</div>
        </div>
        <input type="date" min={minDate} value={form.next_follow_up}
          onChange={e => set("next_follow_up", e.target.value)}
          style={{ ...inputStyle, borderColor: form.next_follow_up ? "#7c3aed" : "#e2e8f0" }} />
        {form.next_follow_up && (
          <div style={{ marginTop: 8, fontSize: 12, color: "#7c3aed", fontWeight: 600 }}>
            Follow-up scheduled for {new Date(form.next_follow_up + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
          </div>
        )}
      </div>

      <button onClick={handleSubmit} disabled={submitting}
        style={{ width: "100%", padding: "15px", background: accent, color: "#fff", border: "none", borderRadius: 14, fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
        {submitting ? "Saving..." : "Add Lead"}
      </button>
    </div>
  );
}
